import { animate, state, style, transition, trigger } from "@angular/animations";
import { AfterViewInit, Component, ElementRef, Input, SimpleChanges, ViewChild, ViewEncapsulation } from "@angular/core";
import { fromEvent, Subscription } from "rxjs";
import { debounceTime, filter } from "rxjs/operators";

@Component({
    selector: "kno2-vertical-split-pane",
    template: `
        <div
            #contentContainer
            [style]="containerStyles"
            class="pane-parent">
            <ng-content select='"kno2-top-pane"'></ng-content>
            <div
                #bottomPane
                [@popOverState]="{
                    value: animationState,
                    params: { expandedPaneHeight: expandedPaneHeight }
                }"
                class="bottom-pane">
                @if (toggleAnimation !== undefined) {
                    <div class="toggle-button">
                        <button
                            nbButton
                            size="small"
                            status="primary"
                            (click)="toggleBottomPane()"
                            class="m-1">
                            {{ animationState === "collapsed" ? showButtonText : hideButtonText }}
                        </button>
                    </div>
                }
                <div>
                    <ng-content select='"kno2-bottom-pane"'></ng-content>
                </div>
            </div>
        </div>
    `,
    styleUrls: ["./vertical-split-pane.component.scss"],
    encapsulation: ViewEncapsulation.Emulated,
    animations: [
        trigger("popOverState", [
            state(
                "expanded",
                style({
                    height: "{{expandedPaneHeight}}px",
                    width: "100%",
                    position: "absolute",
                    left: "0",
                    right: "0",
                    top: 0,
                    background: "white"
                }),
                { params: { expandedPaneHeight: 2 } }
            ),
            state(
                "collapsed",
                style({
                    height: "auto",
                    overflow: "hidden"
                })
            ),
            transition("collapsed => expanded", animate("150ms ease-out")),
            transition("expanded => collapsed", animate("100ms ease-in"))
        ])
    ],
    standalone: false
})
export class VerticalSplitPaneComponent implements AfterViewInit {
    @Input() public showButtonText = "Show Results";
    @Input() public hideButtonText = "Hide Results";
    @Input() public toggleAnimation?: boolean; // setting this disables hover animation
    @ViewChild("contentContainer") private contentContainer: ElementRef;
    @ViewChild("bottomPane") private bottomPane: ElementRef;

    // animation state
    public animationState: "collapsed" | "expanded" = "collapsed";
    public containerStyles = "height:auto";
    public expandedPaneHeight = 0;
    private containerHeight = 0;
    private subscriptions: Array<Subscription> = [];

    public ngAfterViewInit(): void {
        // calc container/bottom pane heights (prevents unexpected UI jittering when in transition)
        this.containerHeight = this.contentContainer.nativeElement.getBoundingClientRect().height;
        this.containerStyles = `height:${this.containerHeight}px`;
        this.expandedPaneHeight = this.containerHeight;

        this.subscriptions.push(
            // on hover
            fromEvent(this.bottomPane.nativeElement, "mouseover")
                .pipe(
                    debounceTime(500),
                    filter(() => this.animationState !== "expanded" && this.toggleAnimation === undefined)
                )
                .subscribe(() => this.onHover()),
            // on leave
            fromEvent(this.bottomPane.nativeElement, "mouseleave")
                .pipe(
                    debounceTime(500),
                    filter(() => this.animationState !== "collapsed" && this.toggleAnimation === undefined)
                )
                .subscribe(() => this.onLeave())
        );
    }

    public ngOnChanges(changes: SimpleChanges): void {
        const toggleChanges = changes["toggleAnimation"];
        if (toggleChanges?.currentValue) {
            this.onHover();
        } else this.onLeave();
    }

    public toggleBottomPane = () => {
        if (this.animationState === "collapsed") {
            this.onHover();
        } else this.onLeave();
    };

    public onHover = () => {
        if (this.animationState !== "expanded") this.animationState = "expanded";
    };

    public onLeave = () => {
        if (this.animationState !== "collapsed") this.animationState = "collapsed";
    };
}
